/**
 * Recipe registry — derived from items.ts.
 *
 * Lives in `shared/` so server (recipe dispatch, ingredient checks, gating)
 * and client (craft-menu render under building UI) read the same row. One
 * recipe = one item output. Recipes are NOT hand-listed: `RECIPES` is built
 * at module load by walking `ITEMS` and emitting one row per Smithy/Workbench/
 * Cooking item that has a non-empty `craftingCost`. Adding a new craftable to
 * `items-data.json` automatically generates a recipe — no second-file leak.
 * If you find yourself editing a hand-listed recipe table somewhere, you've
 * hit a leak; fix the leak. See `docs/build/data-driven-features.md`.
 *
 * Why derivation instead of a hand-table:
 *   - 72+ recipes hand-written would be unreadable and lossy.
 *   - The KTS extraction already carries `craftingCost`, `prepTimeSec`, and
 *     `artisanshipReq` per item; recipes.ts owns *only* the gating
 *     (building + Artisanship level) on top of that data.
 *   - The source field on each item already tells us which station crafts
 *     it — the mapping is data, not code.
 *
 * Sources covered in v1:
 *   - Smithy       -> Forge         (weapons / armor)
 *   - Workbench    -> Workbench     (wood / leather / cloth / bone)
 *   - Cooking      -> Campfire      (foods — B5 cooking-craft, 2026-06-12; the 15
 *     Decision-D cooking recipes whose ingredient keys were remapped to
 *     obtainable ItemIds in items-data.json so the cost classifier resolves
 *     them via the backpack/RESOURCE_POLICY paths)
 *   - HuntersLodge -> HuntersLodge  (pets — ObedienceRod, HuntingMachete;
 *     unified Lodge craft+mastery menu, 2026-06-19)
 *
 * Sources NOT covered in v1 (deferred per Economy Foundation plan):
 *   - Tavern       (abilities-system dependency — spells not built yet)
 *   - Drop / Boss / Quest / Starter / RawResource / Tier-Upgrade / Misc /
 *     Hidden / Debug — not crafted by player; data rows only.
 *
 *   When those dependencies land, extending coverage = adding the source ->
 *   buildingType branch in `deriveRecipes()`. The qa-recipes contract will
 *   catch any item that becomes craftable but has no recipe row.
 *
 * D11 (Artisanship gating): we lift `artisanshipReq` from items-data.json
 * unchanged. Until in-game expertise progression ships, the player-level
 * source is a constant high enough to allow all recipes; flip to a
 * schema-field once expertise lands.
 *
 * Why `RecipeId = string` (not a literal union): `RECIPES` is built at module
 * load from a JSON-shaped record, so `keyof typeof RECIPES` would not yield a
 * tight literal. Runtime validation by `RECIPES[id] !== undefined` is fine
 * for v1 — same trade-off as ItemId in items.ts.
 *
 * Recipe IDs follow the format `craft-${ItemId}` so dispatching from a craft
 * UI knows which item the recipe outputs without a second lookup.
 */
import { ITEMS, type ItemId } from "./items";
import type { BuildingType, BuildingRole } from "./buildings";

export interface RecipeDef {
  id: string;
  /** Item produced. Must satisfy `ITEMS[outputItemId] !== undefined`. */
  outputItemId: ItemId;
  /** Quantity produced per craft. v1 KTS recipes always 1; reserved for
   *  later (e.g., bulk-crafted ammo). */
  outputCount: number;
  /** Station required to dispatch the recipe. Smithy items -> Forge,
   *  Workbench items -> Workbench, Cooking items -> Campfire. */
  buildingType: BuildingType;
  /** Station's role in the building taxonomy. All current recipes are
   *  "craft"; reserved for future shop/storage-driven recipes. */
  buildingRole: BuildingRole;
  /** Artisanship skill required before the recipe surfaces in the menu.
   *  0 = no gate. Lifted verbatim from `ItemDef.artisanshipReq`. */
  artisanshipReq: number;
  /** Cooking skill (KTS units) required before a Campfire recipe surfaces.
   *  0 = no gate. Lifted verbatim from `ItemDef.cookingReq`. Only non-zero on
   *  Campfire (cooking) recipes; compared against `Cooking_tier × 5`. */
  cookingReq: number;
  /** Crafting wind-up in seconds. 0 = instant. Lifted verbatim from
   *  `ItemDef.prepTimeSec`. */
  prepSec: number;
}

/** Recipe-id alias. Runtime-validated via `RECIPES[id] !== undefined`. */
export type RecipeId = string;

function deriveRecipes(): Record<string, RecipeDef> {
  const out: Record<string, RecipeDef> = {};
  for (const item of Object.values(ITEMS)) {
    // Skip items with no crafting cost — drop-only / starter / quest / etc.
    if (Object.keys(item.craftingCost).length === 0) continue;

    let buildingType: BuildingType;
    if (item.source === "Smithy")              buildingType = "Forge";
    else if (item.source === "Workbench")     buildingType = "Workbench";
    else if (item.source === "Cooking")       buildingType = "Campfire";
    else if (item.source === "HuntersLodge") buildingType = "HuntersLodge";
    else continue;  // every other source: not craftable in v1 (see header).

    // Campfire (cooking) recipes are role "warmth"; Forge/Workbench/HuntersLodge are "craft".
    // The proximity check in handleCraft only keys off buildingType, so role is
    // metadata here, but kept accurate for UI grouping + the qa-recipes contract.
    const buildingRole: BuildingRole = buildingType === "Campfire" ? "warmth" : "craft";

    out[`craft-${item.id}`] = {
      id: `craft-${item.id}`,
      outputItemId: item.id,
      outputCount: 1,
      buildingType,
      buildingRole,
      artisanshipReq: item.artisanshipReq ?? 0,
      cookingReq: item.cookingReq ?? 0,
      prepSec: item.prepTimeSec ?? 0,
    };
  }
  return out;
}

export const RECIPES: Readonly<Record<string, RecipeDef>> = deriveRecipes();
